import { Surgery } from 'common/types'
import { TProcedureFilters } from 'lib/hooks/useFilters'

// Constants for hospital names (consistent with ProcedureListTable logic)
const HOSPITAL_NAMES = [
  'General',
  'Community',
  'Childrens',
  'UCLA',
  'USC',
] as const

/**
 * Generates a deterministic hospital name based on surgery ID
 * This ensures the same surgery always gets the same hospital name
 * @param surgeryId - The unique identifier for the surgery
 * @returns A consistent hospital name for the given surgery
 */
const getHospitalNameForSurgery = (surgeryId: string): string => {
  // Create a simple hash from the surgery ID
  let hash = 0
  for (let i = 0; i < surgeryId.length; i++) {
    const char = surgeryId.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }

  // Use absolute value and modulo to get a consistent index
  const index = Math.abs(hash) % HOSPITAL_NAMES.length
  return HOSPITAL_NAMES[index]
}

export const filterSurgeries = (
  surgeries: Surgery[],
  filters: TProcedureFilters
): Surgery[] => {
  if (!surgeries || !surgeries.length) {
    return []
  }

  const hasActiveFilters =
    (filters.locations && filters.locations.length > 0) ||
    (filters.rooms && filters.rooms.length > 0) ||
    !!filters.surgeonName ||
    !!filters.surgeryName

  if (!hasActiveFilters) {
    return surgeries
  }

  return surgeries.filter(({ visit, procedures, _id }) => {
    if (filters.locations.length > 0) {
      const department = visit?.location?.department
      if (!department || !filters.locations.includes(department)) {
        return false
      }
    }

    if (filters.rooms.length > 0) {
      const hospitalName = getHospitalNameForSurgery(_id)
      if (!filters.rooms.includes(hospitalName)) {
        return false
      }
    }

    if (filters.surgeonName) {
      const provider = visit?.attendingProvider
      if (!provider) {
        return false
      }

      const fullName = `${provider.firstName || ''} ${
        provider.lastName || ''
      }`.trim()
      if (!fullName || fullName !== filters.surgeonName) {
        return false
      }
    }

    if (filters.surgeryName) {
      if (!procedures || !procedures.length) {
        return false
      }

      const hasMatchingProcedure = procedures.some(
        (procedure) =>
          procedure.description && procedure.description === filters.surgeryName
      )

      if (!hasMatchingProcedure) {
        return false
      }
    }

    return true
  })
}
